import { ClientTimestamp } from '../shared-types.js';
import { CustomField } from '../shared/custom-field.model.js';
import { AssetFile } from '../shared/index.js';

export enum TenantFeature {
  partnerPortal = 'PARTNER_PORTAL',
  consentManager = 'CONSENT_MANAGER',
  dam = 'DAM',
  guides = 'GUIDES',
}

export enum TenantToggle {
  aiPrompts = 'AI_PROMPTS',
  assetAutoTagModels = 'ASSET_AUTO_TAG_MODELS',
  assetFaceSuggestions = 'ASSET_FACE_SUGGESTIONS',
  videoUpload = 'VIDEO_UPLOAD',
  minorModels = 'MINOR_MODELS',
  modelConsentAssets = 'MODEL_CONSENT_ASSETS',
  modelConsentAssetsDownload = 'MODEL_CONSENT_ASSETS_DOWNLOAD',
  waitingRoom = 'WAITING_ROOM',
  consentProjectsOnly = 'CONSENT_PROJECTS_ONLY',
  monitoringProjectsOnly = 'MONITORING_PROJECTS_ONLY',
  aiGeneratedAltText = 'AI_GENERATED_ALT_TEXT',
  aiTagSuggestions = 'AI_TAG_SUGGESTIONS',
  hideModelsForDamOnlyUsers = 'HIDE_MODELS_FOR_DAM_ONLY_USERS',
  faceDetectionInVideos = 'FACE_DETECTION_IN_VIDEOS',
  mandatoryProjectPurposes = 'MANDATORY_PROJECT_PURPOSES',
  mandatoryProjectMediums = 'MANDATORY_PROJECT_MEDIUMS',
}

export const tenantToggleGroups = [
  {
    name: 'AI',
    toggle: [
      TenantToggle.aiPrompts,
      TenantToggle.assetAutoTagModels,
      TenantToggle.assetFaceSuggestions,
      TenantToggle.aiGeneratedAltText,
      TenantToggle.aiTagSuggestions,
    ],
  },
  {
    name: 'DAM',
    toggle: [
      TenantToggle.videoUpload,
      TenantToggle.waitingRoom,
      TenantToggle.hideModelsForDamOnlyUsers,
    ],
  },
  {
    name: 'CONSENT_MANAGER',
    toggle: [
      TenantToggle.minorModels,
      TenantToggle.modelConsentAssets,
      TenantToggle.modelConsentAssetsDownload,
      TenantToggle.consentProjectsOnly,
      TenantToggle.monitoringProjectsOnly,
      TenantToggle.faceDetectionInVideos,
      TenantToggle.mandatoryProjectPurposes,
      TenantToggle.mandatoryProjectMediums,
    ],
  },
];

export enum TenantLanguage {
  english = 'EN',
  dutch = 'NL',
  norwegian = 'NO',
}

export interface TenantFprintConfig {
  apiKey: string;
  increasedMaxSize?: boolean;
  showUnverifiedMatches?: boolean;
}

export interface TenantFrameSeekConfig {
  tenantName: string;
  quota?: number;
}

export interface TenantLythoConfig {
  apiKey: string;
  lythoUrl: string;
}

export interface TenantBynderConfig {
  bynderUrl: string;
  clientId: string;
  clientSecret: string;
  waitingRoom?: boolean;
}

export interface TenantMediafilerConfig {
  apiKey: string;
  apiUser: string;
  mediafilerUrl: string;
}

export interface TenantProvider {
  type: 'microsoft' | 'saml';
  tenantId?: string;
  providerId?: string;
  label?: string;
}

export interface Tenant {
  id?: string;
  name: string;
  creationDate: ClientTimestamp;
  features: TenantFeature[];
  lythoConfig?: TenantLythoConfig;
  bynderConfig?: TenantBynderConfig;
  mediafilerConfig?: TenantMediafilerConfig;
  deleted?: boolean;
  validSignConfig?: {
    apiKey: string;
    validSignUrl: string;
  };
  customProjectFields?: CustomField[];
  customModelFields?: CustomField[];
  customAssetFields?: CustomField[];
  logo?: AssetFile[];
  logoSmall?: AssetFile[];
  loginBackground?: AssetFile[];
  loginMessage?: string;
  brandColor?: string;
  websiteUrl?: string;
  projectExpireNotification?: number[];
  emailSignature?: string;
  nameSub?: string;
  identityProviders?: TenantProvider[];
  toggles?: TenantToggle[];
  fprintConfig?: TenantFprintConfig;
  fseekConfig?: TenantFrameSeekConfig;
  defaultSsoRoleId?: string;
  languages?: TenantLanguage[];
  defaultLanguage?: TenantLanguage;
  homepageGuideIds?: string[];
}

export interface TenantInfo {
  tenantId: string;
  emailSignIn?: boolean;
  name: string;
  logo?: AssetFile[];
  logoSmall?: AssetFile[];
  loginBackground?: AssetFile[];
  loginMessage?: string;
  brandColor?: string;
  deleted?: boolean;
  features: TenantFeature[];
  toggles?: TenantToggle[];
  identityProviders?: TenantProvider[];
}
